/** @file Applies a batch of commands sequentially (all or nothing), aggregating patches for one commit. */
import type { Patch } from 'immer';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { CommandCall, Doc } from '@pose2d/format/types';
import { applyCommand, type ApplyEnv } from './applyCommand';
import type { CommandResult } from './commandResult';

export interface BatchOutcome {
  doc: Doc;
  patches: Patch[];
  inverse: Patch[];
  results: CommandResult[];
}

/** applyCommand for batch[index]; a Pose2DError leaves with `params.commandIndex` and `params.command` added (CR-011). */
function applyAt(doc: Doc, cmd: CommandCall, env: ApplyEnv, index: number): ReturnType<typeof applyCommand> {
  try {
    return applyCommand(doc, cmd, env);
  } catch (thrown) {
    const error = toPose2DError(thrown);
    if (error === null) throw thrown;
    throw Object.assign(thrown as Error, { pose2d: { ...error, params: { ...error.params, commandIndex: index, command: cmd.name } } });
  }
}

/**
 * Stops at the first failing command and rethrows; nothing from the batch is kept in that case. The rethrown
 * Pose2DError names the failing command in `params.commandIndex` (0-based) and `params.command`; its `path` stays
 * relative to that command's input (CR-011).
 * Patches are copied with loops, never spread as call arguments: one `project.setFps` over 150k keys yields
 * 150k patches, and `push(...patches)` overflows the call stack (CR-003). The inverse lists the last command's
 * inverse first, each command's own order kept.
 */
export function applyBatch(doc: Doc, batch: CommandCall[], env: ApplyEnv): BatchOutcome {
  let current = doc;
  const patches: Patch[] = [];
  const inverses: Patch[][] = [];
  const results: CommandResult[] = [];
  for (const [index, cmd] of batch.entries()) {
    const out = applyAt(current, cmd, env, index);
    current = out.doc;
    for (const p of out.patches) patches.push(p);
    inverses.push(out.inverse);
    results.push(out.result);
  }
  const inverse: Patch[] = [];
  for (let i = inverses.length - 1; i >= 0; i--) for (const p of inverses[i] ?? []) inverse.push(p);
  return { doc: current, patches, inverse, results };
}
